import { clsx } from "clsx"
import {
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  Suspense,
} from "react"

import { type SX, type ThemeKeys } from "../../theme"
import { ButtonGroup } from "../ButtonGroup"
import { IconButton } from "../IconButton"
import { type ImageProps, SuspenseImage } from "../Image"
import { Skeleton } from "../Skeleton"
import { Text, type TextProps } from "../Text"
import styles from "./Card.module.css"

export interface CardProps {
  children?: ReactNode
  color?:
    | "grey1"
    | "grey2"
    | "grey3"
    | "indigo2"
    | "purple1"
    | "purple2"
    | "tomato2"
  variant?:
    | "animated-light"
    | "animated-dark"
    | "dashed"
    | "elevated"
    | "highlighted"
    | "outlined"
    | "selected"
  radius?: 4 | 6
  gap?: 0 | 8 | 12 | 16 | 28 | 32
  paddingBlock?: 0 | 8 | 12 | 16 | 28 | 32
  paddingInline?: 0 | 8 | 16 | 24 | 32
  paddingLeft?: 0 | 16 | 32
  paddingRight?: 0 | 16 | 32
  padding?: 0 | 12 | 16
  sx?: SX
  onSelect?: (event: MouseEvent | KeyboardEvent) => void
  onClose?: () => void
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  function Card(props, ref) {
    const {
      color = "grey1",
      variant = "elevated",
      radius = 6,
      gap,
      padding,
      paddingBlock = 8,
      paddingInline = 16,
      paddingLeft = padding ?? paddingInline,
      paddingRight = padding ?? paddingInline,
      sx,
      children,
      onSelect,
      onClose,
    } = props

    const interactive = Boolean(onSelect)
    const isCloseable = Boolean(onClose)

    const computedPaddingBlock = padding ?? paddingBlock
    const computedGap = gap ?? computedPaddingBlock

    function onKeyDown(event: KeyboardEvent) {
      if (event.code === "Enter" || event.code === "Space") onSelect?.(event)
    }

    function onClick(event: MouseEvent) {
      onSelect?.(event)
    }

    return (
      <div
        ref={ref}
        role={interactive ? "button" : "article"}
        tabIndex={interactive ? 0 : -1}
        className={clsx(
          styles.card,
          styles[`variant-${variant}`],
          styles[`color-${color}`],
          styles[`radius-${radius}`],
          styles[`paddingBlock-${computedPaddingBlock}`],
          styles[`gap-${computedGap}`],
          interactive && styles.interactive,
          isCloseable && styles.closeable,
        )}
        style={{
          ...sx,
          "--Card-padding-left": `${paddingLeft}px`,
          "--Card-padding-right": `${paddingRight}px`,
        }}
        onKeyDown={onKeyDown}
        onClick={onClick}
      >
        {isCloseable && (
          <div className={styles.closeButton}>
            <IconButton
              icon="close"
              size="small"
              variant="solid"
              radius="full"
              color="darkGrey"
              onClick={onClose}
            />
          </div>
        )}
        {children}
      </div>
    )
  },
)

interface CardHeaderProps {
  children: ReactNode
  sx?: SX
}

export function CardHeader(props: CardHeaderProps) {
  const { children, sx } = props

  return (
    <header className={styles.header} style={sx}>
      {children}
    </header>
  )
}

interface CardHeaderTitleProps {
  children?: ReactNode
  variant?: TextProps["variant"]
  sx?: SX
}

export function CardHeaderTitle(props: CardHeaderTitleProps) {
  const { children, variant, sx } = props

  return (
    <Text {...props} noSelect sx={{ flexGrow: 1, ...sx }} variant={variant}>
      {children}
    </Text>
  )
}

interface CardHeaderActionsProps {
  children?: ReactNode
  sx?: SX
}

export function CardHeaderActions(props: CardHeaderActionsProps) {
  const { sx, children } = props

  return (
    <ButtonGroup sx={sx} size="small" color="grey">
      {children}
    </ButtonGroup>
  )
}

interface CardContentProps {
  children?: ReactNode
  sx?: SX
  gap?: ThemeKeys<"space", 0 | 4 | 8 | 12 | 16>
  padding?: 0 | 32
}

export function CardContent(props: CardContentProps) {
  const { sx, children, gap = 8, padding } = props

  return (
    <section className={styles.content} style={{ padding, gap, ...sx }}>
      {children}
    </section>
  )
}

interface CardImageProps extends ImageProps {
  height: 280
}

export function CardImage(props: CardImageProps) {
  const { height, ...imageProps } = props

  return (
    <div className={styles.imageContainer} style={{ height }}>
      <Suspense
        fallback={<Skeleton width="100%" height="100%" borderRadius={0} />}
      >
        <SuspenseImage sizing="cover" {...imageProps} />
      </Suspense>
    </div>
  )
}

interface CardFooterProps {
  children?: ReactNode
  /**
   * Determines if the footer should have padding.
   * @default true
   */
  hasPadding?: boolean
  sx?: SX
  separator?: "solid" | "dashed" | "none"
}

export function CardFooter(props: CardFooterProps) {
  const { sx, hasPadding = true, children, separator = "solid" } = props

  return (
    <footer
      className={clsx(
        styles.footer,
        hasPadding && styles.footerPadding,
        separator !== "none" && styles[`footerSeparator-${separator}`],
      )}
      style={sx}
    >
      {children}
    </footer>
  )
}
